import { Button } from '@/components/ui/button';
import { Sparkles, ArrowRight } from 'lucide-react';
import { useInViewAnimation } from '@/hooks/useInViewAnimation';

interface FinalCTASectionProps {
  onStartCreating: () => void;
  headline?: string;
  description1?: string;
  description2?: string;
  ctaText?: string;
  footerText?: string;
}

export function FinalCTASection({ 
  onStartCreating,
  headline = "Ready to turn your idea into a video?",
  description1,
  description2,
  ctaText = "Create Prompts",
  footerText = "Free to start • No watermarks • Export to Powtoon"
}: FinalCTASectionProps) {
  const { ref, isInView } = useInViewAnimation({ threshold: 0.2 });

  return (
    <section 
      ref={ref}
      className="relative py-24 sm:py-32 overflow-hidden bg-primary"
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -right-24 w-64 h-64 bg-primary-foreground/10 rounded-full blur-3xl animate-float" />
        <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-primary-foreground/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/3 w-48 h-48 bg-primary-foreground/5 rounded-full blur-2xl animate-float" style={{ animationDelay: '2s' }} />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
        <h2 
          className={`text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-primary-foreground mb-8 leading-tight opacity-0 ${
            isInView ? 'animate-fade-in-up' : ''
          }`}
        >
          {headline}
        </h2>

        {description1 && (
          <p 
            className={`text-base sm:text-lg text-primary-foreground/90 max-w-3xl mx-auto mb-4 opacity-0 ${
              isInView ? 'animate-fade-in-up' : ''
            }`}
            style={{ animationDelay: '100ms' }}
          >
            {description1}
          </p>
        )}

        {description2 && (
          <p 
            className={`text-base sm:text-lg text-primary-foreground/90 font-medium max-w-3xl mx-auto mb-8 opacity-0 ${
              isInView ? 'animate-fade-in-up' : ''
            }`}
            style={{ animationDelay: '150ms' }}
          >
            {description2}
          </p>
        )}

        <div 
          className={`opacity-0 ${isInView ? 'animate-fade-in-up' : ''}`}
          style={{ animationDelay: '250ms' }}
        >
          <Button
            size="lg"
            onClick={onStartCreating}
            variant="secondary"
            className="px-12 py-8 text-lg font-semibold rounded-xl shadow-2xl group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 animate-glow-pulse"
          >
            <Sparkles className="w-5 h-5 mr-2" />
            {ctaText}
            <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
          </Button>
        </div>

        {footerText && (
          <p 
            className={`mt-10 text-primary-foreground/60 text-sm opacity-0 ${
              isInView ? 'animate-fade-in-up' : ''
            }`}
            style={{ animationDelay: '350ms' }}
          >
            {footerText}
          </p>
        )}
      </div>
    </section>
  );
}
